import { useCallback, useEffect, useMemo, useState } from "react";
import { getIn, useFormik } from "formik";
import * as yup from "yup";
import {
  IconArrowLeft,
  IconChevronDown,
  IconChevronUp,
  IconDeviceFloppy,
  IconMessageCircleStar,
  IconPlus,
  IconStar,
  IconStarFilled,
  IconTrash,
} from "@tabler/icons-react";
import "@assets/css/before_you_go/panel.css";
import "@assets/css/mode_tool/panel.css";
import "@assets/css/reviews/panel.css";
import InlineError from "@components/forms/InlineError";
import ConfirmDeleteModal from "@components/modals/ConfirmDeleteModal";
import useStartPointLanguages from "@hooks/start_point/useStartPointLanguages";
import {
  deleteRatingRequest,
  listRatingsRequest,
  saveRatingRequest,
} from "@services/ratings/ratingsApi";

function readLocalizedValue(entry) {
  if (typeof entry === "string") return entry;
  if (entry && typeof entry === "object") return entry.value || "";
  return "";
}

function pickTourName(tour = {}) {
  return (
    readLocalizedValue(tour?.name?.en) ||
    readLocalizedValue(tour?.name?.es) ||
    readLocalizedValue(Object.values(tour?.name || {})[0]) ||
    tour?.cityName ||
    "Tour"
  );
}

function toDatetimeLocalValue(value) {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function toIsoDateTime(value) {
  if (!value) return new Date().toISOString();
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return new Date().toISOString();
  return date.toISOString();
}

function createEmptyReview(languageList = [], order = 1) {
  return {
    clientId: `review-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    id: "",
    tour_id: "",
    order,
    reviewDateTime: toDatetimeLocalValue(),
    title: languageList.reduce((acc, language) => {
      acc[language.iso] = {
        message: "",
        id: "",
      };
      return acc;
    }, {}),
    personName: "",
    message: languageList.reduce((acc, language) => {
      acc[language.iso] = {
        message: "",
        id: "",
        first_message: language.iso === "en",
      };
      return acc;
    }, {}),
    rating: 1,
  };
}

function normalizeReviewItem(item = {}, languageList = [], index = 0) {
  return {
    clientId:
      item?.clientId ||
      item?.id ||
      `review-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    id: String(item?.id || ""),
    tour_id: String(item?.tour_id || ""),
    order: Number(item?.order || 0) || index + 1,
    reviewDateTime: toDatetimeLocalValue(
      item?.updateAt ||
        item?.update_at ||
        item?.updatedAt ||
        item?.create_at ||
        item?.createdAt ||
        "",
    ),
    title: languageList.reduce((acc, language) => {
      const localizedValue = item?.title?.[language.iso];
      acc[language.iso] = {
        message: String(localizedValue?.message || ""),
        id: String(localizedValue?.id || ""),
      };
      return acc;
    }, {}),
    personName: String(item?.person_name || item?.personName || ""),
    message: languageList.reduce((acc, language) => {
      const localizedValue = item?.message?.[language.iso];
      acc[language.iso] = {
        message: String(localizedValue?.message || ""),
        id: String(localizedValue?.id || ""),
        first_message: language.iso === "en",
      };
      return acc;
    }, {}),
    rating: Math.min(5, Math.max(1, Number(item?.rating || 1) || 1)),
  };
}

function buildRatingPayload(item = {}, tourId = "", languageList = []) {
  const isUpdate = Boolean(String(item?.id || "").trim());
  const selectedDate = toIsoDateTime(item?.reviewDateTime || "");

  return {
    tour_id: String(tourId || ""),
    id: String(item?.id || ""),
    create_at: isUpdate ? "" : selectedDate,
    updateAt: isUpdate ? selectedDate : "",
    title: languageList.reduce((acc, language) => {
      acc[language.iso] = {
        message: String(item?.title?.[language.iso]?.message || ""),
        id: String(item?.title?.[language.iso]?.id || ""),
      };
      return acc;
    }, {}),
    person_name: String(item?.personName || ""),
    message: languageList.reduce((acc, language) => {
      acc[language.iso] = {
        message: String(item?.message?.[language.iso]?.message || ""),
        id: String(item?.message?.[language.iso]?.id || ""),
        first_message: language.iso === "en",
      };
      return acc;
    }, {}),
    rating: Math.min(5, Math.max(1, Number(item?.rating || 1) || 1)),
  };
}

function buildValidationSchema(languageList = []) {
  const localizedShape = languageList.reduce((acc, language) => {
    acc[language.iso] =
      language.iso === "en"
        ? yup.object({
            message: yup.string().trim().required("EN message is required."),
            id: yup.string().default(""),
            first_message: yup.boolean().default(true),
          })
                : yup.object({
                    message: yup.string().default(""),
                    id: yup.string().default(""),
                    first_message: yup.boolean().default(false),
                  });
    return acc;
  }, {});

  const titleShape = languageList.reduce((acc, language) => {
    acc[language.iso] = yup.object({
      message: yup.string().default(""),
      id: yup.string().default(""),
    });
    return acc;
  }, {});

  return yup.object({
    items: yup.array().of(
      yup.object({
        title: yup.object().shape(titleShape),
        personName: yup.string().default(""),
        reviewDateTime: yup.string().default(""),
        message: yup.object().shape(localizedShape),
        rating: yup
          .number()
          .min(1, "Rating must be between 1 and 5.")
          .max(5, "Rating must be between 1 and 5.")
          .required("Rating is required."),
      }),
    ),
  });
}

function ReviewsPanel({ activeTour, languages, onClose }) {
  const languageList = useStartPointLanguages(languages);
  const [activeLanguageIso, setActiveLanguageIso] = useState("");
  const [reviewItems, setReviewItems] = useState([]);
  const [expandedItemId, setExpandedItemId] = useState("");
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [submittingReviewId, setSubmittingReviewId] = useState("");
  const [deletingReviewId, setDeletingReviewId] = useState("");
  const [deleteTargetItem, setDeleteTargetItem] = useState(null);
  const [localError, setLocalError] = useState("");
  const validationSchema = useMemo(
    () => buildValidationSchema(languageList),
    [languageList],
  );
  const activeTourId = useMemo(
    () =>
      String(
        activeTour?.id || activeTour?.tur_id || activeTour?.tourId || "",
      ),
    [activeTour],
  );

  useEffect(() => {
    if (!languageList.length) {
      setActiveLanguageIso("");
      return;
    }
    setActiveLanguageIso((currentIso) => {
      if (languageList.some((language) => language.iso === currentIso)) {
        return currentIso;
      }
      return languageList[0].iso;
    });
  }, [languageList]);

  const currentLanguageIso = useMemo(() => {
    if (languageList.some((language) => language.iso === activeLanguageIso)) {
      return activeLanguageIso;
    }
    return languageList[0]?.iso || "";
  }, [activeLanguageIso, languageList]);
  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      items: reviewItems,
    },
    validationSchema,
    onSubmit: () => {},
  });

  const fetchRatings = useCallback(async () => {
    if (!activeTourId || !languageList.length) return;
    setLoadingReviews(true);
    setLocalError("");
    try {
      const response = await listRatingsRequest(activeTourId);
      const normalized = (Array.isArray(response?.body) ? response.body : [])
        .map((item, index) => normalizeReviewItem(item, languageList, index));
      setReviewItems(normalized);
      formik.setFieldValue("items", normalized, false);
    } catch (error) {
      setReviewItems([]);
      formik.setFieldValue("items", [], false);
      setLocalError(error?.message || "Could not load reviews.");
    } finally {
      setLoadingReviews(false);
    }
  }, [activeTourId, languageList]);

  useEffect(() => {
    fetchRatings();
  }, [fetchRatings]);

  if (!activeTour) return null;

  function handleAddReview() {
    const nextItem = createEmptyReview(languageList, reviewItems.length + 1);
    nextItem.tour_id = activeTourId;
    const nextItems = [...reviewItems, nextItem];
    setReviewItems(nextItems);
    formik.setFieldValue("items", nextItems, true);
    setExpandedItemId(nextItem.clientId);
  }

  function handleRemoveReview(clientId) {
    const targetItem = reviewItems.find((item) => item.clientId === clientId);
    if (targetItem?.id) {
      setDeletingReviewId(clientId);
      setLocalError("");
      return deleteRatingRequest(targetItem.id)
        .then(() => {
          fetchRatings();
          setExpandedItemId((current) => (current === clientId ? "" : current));
        })
        .catch((error) => {
          setLocalError(error?.message || "Could not delete review.");
        })
        .finally(() => {
          setDeletingReviewId("");
        });
    }
    const nextItems = reviewItems
      .filter((item) => item.clientId !== clientId)
      .map((item, index) => ({
        ...item,
        tour_id: item?.tour_id || activeTourId,
        order: index + 1,
      }));
    setReviewItems(nextItems);
    formik.setFieldValue("items", nextItems, true);
    formik.setTouched({
      items: nextItems.map(() => ({
        title: { en: true },
        personName: true,
        content: { en: true },
        rating: true,
      })),
    });
    setExpandedItemId((current) => (current === clientId ? "" : current));
    return Promise.resolve();
  }

  function handleRequestDeleteReview(clientId) {
    const targetItem = reviewItems.find((item) => item.clientId === clientId);
    if (!targetItem) return;
    setDeleteTargetItem(targetItem);
    setLocalError("");
  }

  function getDeletePreviewName(item = {}) {
    return (
      String(item?.message?.en?.message || "").trim() ||
      String(item?.message?.[currentLanguageIso]?.message || "").trim() ||
      `Review ${item?.order || ""}`.trim()
    );
  }

  function handleCloseDeleteModal() {
    if (deletingReviewId) return;
    setDeleteTargetItem(null);
  }

  async function handleConfirmDeleteReview() {
    if (!deleteTargetItem?.clientId) return;
    const targetClientId = deleteTargetItem.clientId;
    await handleRemoveReview(targetClientId);
    setDeleteTargetItem(null);
  }

  function updateReviewItems(nextItems = []) {
    setReviewItems(nextItems);
    formik.setFieldValue("items", nextItems, true);
  }

  function updateReviewContent(clientId, iso, value) {
    const nextItems = reviewItems.map((item) =>
        item.clientId === clientId
          ? {
              ...item,
              message: {
                ...(item.message || {}),
                [iso]: {
                  ...(item.message?.[iso] || {
                    id: "",
                    first_message: iso === "en",
                  }),
                  message: value,
                },
              },
            }
          : item,
    );
    updateReviewItems(nextItems);
  }

  function updateReviewTitle(clientId, iso, value) {
    const nextItems = reviewItems.map((item) =>
      item.clientId === clientId
        ? {
            ...item,
            title: {
              ...(item.title || {}),
              [iso]: {
                ...(item.title?.[iso] || {
                  id: "",
                }),
                message: value,
              },
            },
          }
        : item,
    );
    updateReviewItems(nextItems);
  }

  function updateReviewPersonName(clientId, value) {
    const nextItems = reviewItems.map((item) =>
      item.clientId === clientId
        ? {
            ...item,
            personName: value,
          }
        : item,
    );
    updateReviewItems(nextItems);
  }

  function updateReviewDateTime(clientId, value) {
    const nextItems = reviewItems.map((item) =>
      item.clientId === clientId
        ? {
            ...item,
            reviewDateTime: value,
          }
        : item,
    );
    updateReviewItems(nextItems);
  }

  function updateReviewRating(clientId, value) {
    const nextItems = reviewItems.map((item) =>
        item.clientId === clientId
          ? {
              ...item,
              rating: Math.min(5, Math.max(1, Number(value) || 1)),
            }
          : item,
    );
    updateReviewItems(nextItems);
  }

  async function handleSaveReview(clientId, itemIndex) {
    formik.setFieldTouched(`items.${itemIndex}.message.en.message`, true, true);
    formik.setFieldTouched(`items.${itemIndex}.rating`, true, true);
    const errors = await formik.validateForm();
    const itemError = getIn(errors, `items.${itemIndex}`);
    if (itemError) return;
    const item = reviewItems[itemIndex];
    if (!item) return;
    setSubmittingReviewId(clientId);
    setLocalError("");
    try {
      await saveRatingRequest(
        buildRatingPayload(item, activeTourId, languageList),
      );
      await fetchRatings();
      setExpandedItemId(clientId);
    } catch (error) {
      setLocalError(error?.message || "Could not save review.");
    } finally {
      setSubmittingReviewId("");
    }
  }

  function renderRatingLabel(rating = 0) {
    return `${Number(rating) || 0}/5`;
  }

  return (
    <aside className="before-you-go-tool-overlay">
      <div className="mode-route-tool-header before-you-go-tool-header">
        {languageList.length ? (
          <div className="app-language-section">
            <div className="app-language-switcher" aria-label="Languages">
              {languageList.map((language) => (
                <button
                  key={language.id || language.iso}
                  type="button"
                  className={`app-language-btn ${currentLanguageIso === language.iso ? "is-active" : ""}`}
                  onClick={() => setActiveLanguageIso(language.iso)}
                  aria-pressed={currentLanguageIso === language.iso}
                  title={language.name || language.iso.toUpperCase()}
                >
                  {language.iso.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        <div className="mode-route-tool-head">
          <button
            type="button"
            className="app-icon-btn app-icon-btn--back"
            aria-label="Go back"
            title="Go back"
            onClick={onClose}
          >
            <IconArrowLeft size={16} />
          </button>
          <button
            type="button"
            className="app-icon-btn app-icon-btn--update"
            aria-label="Add review"
            title="Add review"
            onClick={handleAddReview}
          >
            <IconPlus size={18} />
          </button>
          <h3 className="mode-route-tool-title before-you-go-tool-title">
            Reviews - {pickTourName(activeTour)}
          </h3>
        </div>
      </div>

      <section className="before-you-go-tool-panel">
        <div className="before-you-go-tool-list reviews-tool-list">
          {reviewItems.length ? (
            reviewItems.map((item) => {
              const itemIndex = reviewItems.findIndex(
                (entry) => entry.clientId === item.clientId,
              );
              const isOpen = expandedItemId === item.clientId;
              const localizedContent =
                String(item?.title?.[currentLanguageIso]?.message || "").trim() ||
                String(item?.title?.en?.message || "").trim() ||
                String(item?.message?.[currentLanguageIso]?.message || "").trim() ||
                String(item?.message?.en?.message || "").trim() ||
                `Review ${item.order}`;
              const messageError = getIn(
                formik.errors,
                `items.${itemIndex}.message.en.message`,
              );
              const messageTouched = getIn(
                formik.touched,
                `items.${itemIndex}.message.en.message`,
              );
              const ratingError = getIn(
                formik.errors,
                `items.${itemIndex}.rating`,
              );
              const ratingTouched = getIn(
                formik.touched,
                `items.${itemIndex}.rating`,
              );
              const isItemInvalid = Boolean(
                getIn(formik.errors, `items.${itemIndex}`),
              );

              return (
                <article
                  key={item.clientId}
                  className="before-you-go-tool-item reviews-tool-item"
                >
                  <div className="before-you-go-tool-item-head">
                    <button
                      type="button"
                      className="before-you-go-tool-accordion"
                      onClick={() =>
                        setExpandedItemId((current) =>
                          current === item.clientId ? "" : item.clientId,
                        )
                      }
                    >
                      <span className="before-you-go-tool-order">
                        <IconMessageCircleStar size={16} />
                      </span>
                      <span className="reviews-tool-accordion-copy">
                        <strong className="reviews-tool-accordion-title">
                          {localizedContent}
                        </strong>
                        <small className="reviews-tool-accordion-meta">
                          {String(item?.personName || "").trim()
                            ? `${item.personName} · `
                            : ""}
                          Rating: {renderRatingLabel(item?.rating)}
                        </small>
                      </span>
                      {isOpen ? (
                        <IconChevronUp size={16} />
                      ) : (
                        <IconChevronDown size={16} />
                      )}
                    </button>
                    <button
                      type="button"
                      className="app-icon-btn app-icon-btn--delete"
                      aria-label="Delete review"
                      title="Delete review"
                      onClick={() => handleRequestDeleteReview(item.clientId)}
                      disabled={deletingReviewId === item.clientId}
                    >
                      {deletingReviewId === item.clientId ? (
                        <span className="app-icon-btn-spinner" />
                      ) : (
                        <IconTrash size={16} />
                      )}
                    </button>
                  </div>

                  {isOpen ? (
                    <div className="before-you-go-tool-form reviews-tool-form">
                      <div className="reviews-tool-fields">
                        <div>
                          <p className="tour-add-form-label">
                            Title ({currentLanguageIso.toUpperCase()})
                          </p>
                          <label className="field-label app-form-field mode-route-field">
                            <input
                              className="field-input"
                              value={String(
                                item?.title?.[currentLanguageIso]?.message || "",
                              )}
                              onChange={(event) =>
                                updateReviewTitle(
                                  item.clientId,
                                  currentLanguageIso,
                                  event.target.value,
                                )
                              }
                              placeholder={`Write title (${currentLanguageIso})`}
                            />
                          </label>
                        </div>
                        <div>
                          <p className="tour-add-form-label">Person Name</p>
                          <label className="field-label app-form-field mode-route-field">
                            <input
                              className="field-input"
                              value={String(item?.personName || "")}
                              onChange={(event) =>
                                updateReviewPersonName(
                                  item.clientId,
                                  event.target.value,
                                )
                              }
                              placeholder="Write person name"
                            />
                          </label>
                        </div>
                        <div>
                          <p className="tour-add-form-label">Date and Time</p>
                          <label className="field-label app-form-field mode-route-field">
                            <input
                              className="field-input"
                              type="datetime-local"
                              value={String(item?.reviewDateTime || "")}
                              onChange={(event) =>
                                updateReviewDateTime(
                                  item.clientId,
                                  event.target.value,
                                )
                              }
                            />
                          </label>
                        </div>
                        <div>
                          <p className="tour-add-form-label">
                            Message ({currentLanguageIso.toUpperCase()})
                          </p>
                          {currentLanguageIso === "en" &&
                          messageTouched &&
                          messageError ? (
                            <InlineError>{messageError}</InlineError>
                          ) : null}
                          <label className="field-label app-form-field mode-route-field">
                          <textarea
                            className="field-input reviews-tool-textarea"
                            rows={6}
                            value={String(
                              item?.message?.[currentLanguageIso]?.message || "",
                            )}
                            onChange={(event) =>
                              updateReviewContent(
                                item.clientId,
                                currentLanguageIso,
                                event.target.value,
                              )
                            }
                            onBlur={() =>
                              currentLanguageIso === "en"
                                ? formik.setFieldTouched(
                                    `items.${itemIndex}.message.en.message`,
                                    true,
                                    true,
                                  )
                                : null
                            }
                            placeholder={`Write review (${currentLanguageIso})`}
                          />
                          </label>
                        </div>
                        <div className="reviews-tool-rating">
                          {ratingTouched && ratingError ? (
                            <InlineError>{ratingError}</InlineError>
                          ) : null}
                          {Array.from({ length: 5 }, (_, index) => {
                            const value = index + 1;
                            const isActive =
                              Number(item?.rating || 1) >= value;
                            const StarIcon = isActive
                              ? IconStarFilled
                              : IconStar;
                            return (
                              <button
                                key={`${item.clientId}-rating-${value}`}
                                type="button"
                                className={`reviews-tool-rating-btn ${Number(item?.rating || 0) === value ? "is-active" : ""}`}
                                onClick={() =>
                                  (() => {
                                    updateReviewRating(
                                      item.clientId,
                                      value,
                                    );
                                    formik.setFieldTouched(
                                      `items.${itemIndex}.rating`,
                                      true,
                                      true,
                                    );
                                  })()
                                }
                                aria-label={`Set ${value} stars`}
                                title={`${value} stars`}
                              >
                                <StarIcon size={18} />
                                <span>{value}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="reviews-tool-save-row">
                        <button
                          type="button"
                          className={`app-icon-btn app-icon-btn--save ${isItemInvalid || submittingReviewId === item.clientId ? "is-disabled" : ""} ${submittingReviewId === item.clientId ? "is-loading" : ""}`}
                          aria-label="Save review"
                          title="Save review"
                          onClick={() =>
                            handleSaveReview(item.clientId, itemIndex)
                          }
                          disabled={
                            isItemInvalid || submittingReviewId === item.clientId
                          }
                          aria-disabled={
                            isItemInvalid || submittingReviewId === item.clientId
                          }
                        >
                          {submittingReviewId === item.clientId ? (
                            <span className="app-icon-btn-spinner" />
                          ) : (
                            <IconDeviceFloppy size={16} />
                          )}
                        </button>
                      </div>
                    </div>
                  ) : null}
                </article>
              );
            })
          ) : (
            <div className="before-you-go-tool-empty reviews-tool-empty">
              {loadingReviews
                ? "Loading reviews..."
                : "No reviews yet. Use the add button to create one."}
            </div>
          )}
          {localError ? (
            <p className="before-you-go-tool-help reviews-tool-error">
              {localError}
            </p>
          ) : null}
        </div>
      </section>
      <ConfirmDeleteModal
        isOpen={Boolean(deleteTargetItem)}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDeleteReview}
        submitting={Boolean(deletingReviewId)}
        title="Delete review"
        description={
          <>
            Are you sure you want to delete{" "}
            <span>{getDeletePreviewName(deleteTargetItem)}</span> review?
          </>
        }
      />
    </aside>
  );
}

export default ReviewsPanel;
